import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FlaskConical, Home, MapPin, Star, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocation as useGeo } from "@/contexts/LocationContext";
import LocationBar from "@/components/LocationBar";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

export default function LabTests() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { loc } = useGeo();
  const [labs, setLabs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [mode, setMode] = useState("home_collection");
  const [slot, setSlot] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const p = loc ? { near_lat: loc.lat, near_lng: loc.lng } : {};
    api.get("/labs", { params: p }).then((r) => setLabs(r.data));
  }, [loc?.lat, loc?.lng]);

  const toggle = (t) => setChosen((c) => c.includes(t) ? c.filter(x => x !== t) : [...c, t]);

  const book = async () => {
    if (!user) { nav("/login"); return; }
    if (!chosen.length || !slot) { toast.error("Select tests and slot"); return; }
    setLoading(true);
    try {
      await api.post("/lab-bookings", {
        lab_id: selected.id, tests: chosen, mode,
        slot_time: new Date(slot).toISOString(), address,
      });
      toast.success(mode === "home_collection" ? "Home collection scheduled" : "Centre visit booked");
      setSelected(null); setChosen([]);
      nav("/patient");
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-teal-700">Diagnostics</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Book lab tests</h1>
          <p className="text-slate-600 text-sm mt-1">Home sample collection or clinic visit — certified partner labs.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/ai-triage"><Button variant="outline" data-testid="lab-ai-cta"><Sparkles className="w-4 h-4 mr-2" />Ask MedHOne AI</Button></Link>
          <LocationBar />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {labs.map((l) => (
          <Card key={l.id} className="p-5 rounded-2xl card-lift" data-testid={`lab-card-${l.id}`}>
            <div className="flex gap-3 items-start">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center text-emerald-700">
                <FlaskConical className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <Link to={`/facility/lab/${l.id}`} className="font-display font-bold hover:text-teal-700 transition-colors" data-testid={`lab-detail-${l.id}`}>{l.name}</Link>
                <div className="text-xs text-slate-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{l.address}</div>
                <div className="mt-1 flex items-center gap-2 text-xs">
                  <Star className="w-3 h-3 fill-amber-500 text-amber-500" />{l.rating}
                  {l.home_collection && <Badge className="bg-emerald-100 text-emerald-800 border-0"><Home className="w-3 h-3 mr-1" />Home collection</Badge>}
                  {l.distance_km != null && <Badge variant="outline" className="text-[10px] py-0" data-testid={`lab-dist-${l.id}`}>{l.distance_km} km</Badge>}
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-1">
              {l.tests?.slice(0, 4).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
              {l.tests?.length > 4 && <Badge variant="outline" className="text-[10px]">+{l.tests.length - 4}</Badge>}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button data-testid={`book-lab-visit-${l.id}`} size="sm" variant="outline" onClick={() => { setSelected(l); setMode("centre_visit"); }}>
                Centre visit
              </Button>
              <Button data-testid={`book-lab-${l.id}`} size="sm" className="bg-emerald-600 text-white" onClick={() => { setSelected(l); setMode("home_collection"); }}>
                Home collection
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{selected?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select tests</Label>
              <div className="mt-2 max-h-52 overflow-y-auto space-y-2">
                {selected?.tests?.map((t) => (
                  <label key={t} className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg">
                    <Checkbox data-testid={`test-check-${t}`} checked={chosen.includes(t)} onCheckedChange={() => toggle(t)} />
                    <span className="text-sm">{t}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Mode</Label>
              <div className="flex gap-2 mt-1">
                <Badge className={mode === "home_collection" ? "bg-teal-600 cursor-pointer" : "bg-slate-200 text-slate-800 cursor-pointer"} onClick={() => setMode("home_collection")} data-testid="lab-mode-home">Home collection</Badge>
                <Badge className={mode === "centre_visit" ? "bg-teal-600 cursor-pointer" : "bg-slate-200 text-slate-800 cursor-pointer"} onClick={() => setMode("centre_visit")} data-testid="lab-mode-visit">Centre visit</Badge>
              </div>
            </div>
            <div>
              <Label>Slot</Label>
              <Input data-testid="lab-slot" type="datetime-local" value={slot} onChange={(e) => setSlot(e.target.value)} />
            </div>
            {mode === "home_collection" && (
              <div>
                <Label>Address</Label>
                <Input data-testid="lab-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Home address" />
              </div>
            )}
            {mode === "centre_visit" && (
              <div className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3">
                📍 Visit at: <strong>{selected?.address}</strong>. Please arrive 10 minutes before your slot.
              </div>
            )}
            <div className="text-sm text-slate-600">
              Estimated total: <span className="font-semibold font-mono">₹{chosen.length * 400}</span>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="lab-confirm" disabled={loading} onClick={book} className="bg-emerald-600 text-white">
              {loading ? "Booking…" : mode === "home_collection" ? "Confirm home collection" : "Confirm centre visit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
